/* eslint-disable react-hooks/rules-of-hooks */
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useSnackbar } from "components/GlobalSnackbar";
import { handleApiError } from "utils/errorHandler";

// @mui material components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";

// API
import { apiGetAllZones, apiSearchZones } from "api/zones";

export default function data({
  onRowSelect,
  selectedRow,
  currentPage,
  itemsPerPage,
  search,
  refreshTrigger,
  editingCell = null,
  editValue = '',
  onCellEdit = () => {},
  onSaveEdit = () => {},
  onCancelEdit = () => {},
  onEditValueChange = () => {},
  onEditKeyDown = () => {},
  saving = false,
}) {
  const [zoneData, setZoneData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { showError } = useSnackbar();

  useEffect(() => {
    const fetchZones = async () => {
      try {
        setLoading(true);
        let response;
        // Always refresh: if search field is empty, show all data (matches Penyewa/Pelanggan behavior)
        if (search && typeof search.zon === "string") {
          const trimmed = search.zon.trim();
          if (trimmed !== "") {
            response = await apiSearchZones({ zon: trimmed }, currentPage, itemsPerPage);
          } else {
            response = await apiGetAllZones(currentPage, itemsPerPage);
          }
        } else {
          response = await apiGetAllZones(currentPage, itemsPerPage);
        }
        if (response.data) {
          setZoneData(response.data);
          setTotalPages(response.pagination.totalPages);
          setTotalItems(response.pagination.totalItems);
        } else {
          setZoneData(response);
        }
      } catch (error) {
        handleApiError(error, showError);
        setZoneData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchZones();
  }, [currentPage, itemsPerPage, refreshTrigger]);

  const ZoneName = ({ name }) => (
    <MDBox display="flex" alignItems="center" lineHeight={1}>
      <MDTypography variant="button" fontWeight="medium" ml={0.05} lineHeight={1}>
        {name}
      </MDTypography>
    </MDBox>
  );

  ZoneName.propTypes = {
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  };

  const generateRows = () => {
    if (loading) {
      return [
        {
          select: (
            <input type="checkbox" checked={false} disabled style={{ cursor: "not-allowed" }} />
          ),
          zon: <ZoneName name="Loading..." />,
        },
      ];
    }

    // No need to filter on frontend, backend search already applied
    return zoneData.map((zone) => {
      const isChecked = !!(selectedRow && selectedRow.id === zone.id);
      return {
        select: (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => {
              if (isChecked) {
                onRowSelect && onRowSelect(null); // Uncheck
              } else {
                onRowSelect && onRowSelect(zone); // Check
              }
            }}
            style={{ cursor: "pointer" }}
          />
        ),
        zon: editingCell?.rowId === zone.id && editingCell?.column === 'zon' ? (
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <TextField
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={onEditKeyDown}
              size="small"
              autoFocus
              disabled={saving}
              fullWidth
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  py: 0.25,
                  px: 0.75,
                }
              }}
            />
            <IconButton size="small" onClick={onSaveEdit} disabled={saving} color="success" sx={{ p: 0.5 }}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancelEdit} disabled={saving} color="error" sx={{ p: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </MDBox>
        ) : (
          <MDBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
              },
              px: 1,
              py: 0.25,
            }}
            onClick={() => onCellEdit(zone.id, 'zon', zone.zon, zone)}
          >
            <MDTypography variant="button" fontWeight="medium">
              {zone.zon || "Unknown"}
            </MDTypography>
            <EditIcon sx={{ fontSize: 14, color: 'action.disabled', opacity: 0.5 }} />
          </MDBox>
        ),
      };
    });
  };

  return {
    columns: [
      { Header: "", accessor: "select", width: "10%", align: "center" },
      { Header: "zon", accessor: "zon", width: "90%", align: "left" },
    ],

    rows: generateRows(),
    pagination: {
      totalPages,
      totalItems,
      currentPage,
      actualItemsOnPage: zoneData.length, // Add actual items displayed on current page
    },
    rawData: zoneData, // Expose raw data for duplicate checking
  };
}
